/**
 * Iter60.p62 — Admin → Leave Types catalog editor.
 *
 * HR + Admin can edit config (paid/unpaid, entitled days, evidence,
 * tenure gate, gender restriction, min/max, hourly meta). Only Admin
 * can create new custom types. The `article_reference` column is
 * documentation-only — the underlying PATCH endpoint drops it from the
 * payload so HR cannot silently rewrite the law citation.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save, ScrollText } from "lucide-react";

import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const GENDER_OPTIONS = [
    { value: "__any__", label: "Any" },
    { value: "male", label: "Male only" },
    { value: "female", label: "Female only" },
];


function toStrNum(v) {
    if (v === null || v === undefined || v === "") return "";
    return String(v);
}

function fromStrNum(v) {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}


export default function LeaveTypes() {
    return (
        <AppShell title="Leave Types — Somali Labor Law Catalog">
            <Body />
        </AppShell>
    );
}


function Body() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;
    const isHR = user?.role === ROLES.KAAFI_HR;
    const canWrite = isAdmin || isHR;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingCode, setSavingCode] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/leave-types");
            setItems(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const patchOne = useCallback(async (code, patch) => {
        setSavingCode(code);
        try {
            await api.patch(`/leave-types/${encodeURIComponent(code)}`, patch);
            toast.success("Leave type updated.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingCode(null);
        }
    }, [load]);

    if (!user) return null;

    if (!canWrite) {
        return (
            <div
                className="asl-card p-8 text-center"
                data-testid="leave-types-forbidden"
            >
                <h2 className="text-lg font-semibold asl-navy-text">Restricted</h2>
                <p className="text-sm asl-text-2 mt-2">
                    Only HR and Admin can view the Leave Types catalog.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="leave-types-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-sm asl-text-2 max-w-3xl">
                        Configure entitlement, evidence, tenure gate, and gender restriction for every leave type. Article
                        references reflect the Somali Labor Law and are locked. Activate/deactivate a type here to control which
                        options appear in the Leave Request dialog fleet-wide.
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        onClick={() => setCreateOpen(true)}
                        className="asl-btn-primary"
                        data-testid="leave-types-create-btn"
                    >
                        <Plus className="h-4 w-4 mr-1" /> New Type
                    </Button>
                )}
            </div>

            <div className="asl-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table
                        className="w-full text-sm"
                        data-testid="leave-types-table"
                    >
                        <thead>
                            <tr
                                className="text-left"
                                style={{
                                    background: "var(--asl-navy-50, #eef1f8)",
                                    borderBottom: "1px solid var(--asl-border)",
                                }}
                            >
                                <th className="px-3 py-3">Type</th>
                                <th className="px-3 py-3 w-24 text-center">Paid</th>
                                <th className="px-3 py-3 w-28">Entitled / yr</th>
                                <th className="px-3 py-3 w-28">Accrual / mo</th>
                                <th className="px-3 py-3 w-24 text-center">Tenure (mo)</th>
                                <th className="px-3 py-3 w-32">Gender</th>
                                <th className="px-3 py-3 w-24">Min / Max</th>
                                <th className="px-3 py-3">Evidence</th>
                                <th className="px-3 py-3 w-28">Hourly</th>
                                <th className="px-3 py-3">Article</th>
                                <th className="px-3 py-3 w-24 text-center">Active</th>
                                <th className="px-3 py-3 w-20 text-right">Save</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={12} className="px-4 py-6 asl-text-2 text-center">
                                        Loading leave types…
                                    </td>
                                </tr>
                            )}
                            {!loading && items.map((t) => (
                                <LeaveTypeRow
                                    key={t.code}
                                    t={t}
                                    onSave={(patch) => patchOne(t.code, patch)}
                                    saving={savingCode === t.code}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isAdmin && (
                <CreateTypeDialog
                    open={createOpen}
                    onOpenChange={setCreateOpen}
                    onCreated={async () => {
                        setCreateOpen(false);
                        await load();
                    }}
                />
            )}
        </div>
    );
}


function LeaveTypeRow({ t, onSave, saving }) {
    // Local draft state keeps edits pending until the row's Save button
    // is clicked. That way HR can review a couple of fields together
    // before firing the PATCH.
    const [draft, setDraft] = useState(() => normaliseDraft(t));
    // Reset draft whenever the incoming type changes (e.g. after save).
    useEffect(() => { setDraft(normaliseDraft(t)); }, [t]);

    const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(normaliseDraft(t)), [draft, t]);

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const submit = () => {
        const patch = {
            paid: draft.paid,
            entitled_days_per_year: fromStrNum(draft.entitled_days_per_year),
            accrual_days_per_month: fromStrNum(draft.accrual_days_per_month),
            tenure_gate_months: fromStrNum(draft.tenure_gate_months) || 0,
            gender_restriction: draft.gender_restriction === "__any__" ? null : draft.gender_restriction,
            min_days: fromStrNum(draft.min_days),
            max_days: fromStrNum(draft.max_days),
            evidence_required: (draft.evidence_required || "").trim() || null,
            is_hourly: !!draft.is_hourly,
            hours_per_day: fromStrNum(draft.hours_per_day),
            duration_days: fromStrNum(draft.duration_days),
            active: !!draft.active,
        };
        onSave(patch);
    };

    return (
        <tr
            className="border-t"
            style={{
                borderColor: "var(--asl-border)",
                opacity: draft.active ? 1 : 0.72,
            }}
            data-testid={`leave-type-row-${t.code}`}
        >
            <td className="px-3 py-3">
                <div className="font-semibold asl-navy-text">{t.name}</div>
                <div className="text-[11px] mono asl-text-3">{t.code}</div>
            </td>
            <td className="px-3 py-3 text-center">
                <Checkbox
                    checked={!!draft.paid}
                    onCheckedChange={(v) => set("paid", !!v)}
                    data-testid={`leave-type-paid-${t.code}`}
                />
            </td>
            <td className="px-3 py-3">
                <Input
                    className="h-9"
                    type="number"
                    value={draft.entitled_days_per_year}
                    onChange={(e) => set("entitled_days_per_year", e.target.value)}
                    placeholder="—"
                    data-testid={`leave-type-entitled-${t.code}`}
                />
            </td>
            <td className="px-3 py-3">
                <Input
                    className="h-9"
                    type="number"
                    step="0.25"
                    value={draft.accrual_days_per_month}
                    onChange={(e) => set("accrual_days_per_month", e.target.value)}
                    placeholder="—"
                    data-testid={`leave-type-accrual-${t.code}`}
                />
            </td>
            <td className="px-3 py-3">
                <Input
                    className="h-9 text-center"
                    type="number"
                    min={0}
                    value={draft.tenure_gate_months}
                    onChange={(e) => set("tenure_gate_months", e.target.value)}
                    data-testid={`leave-type-tenure-${t.code}`}
                />
            </td>
            <td className="px-3 py-3">
                <Select
                    value={draft.gender_restriction || "__any__"}
                    onValueChange={(v) => set("gender_restriction", v)}
                >
                    <SelectTrigger
                        className="h-9"
                        data-testid={`leave-type-gender-${t.code}`}
                    ><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {GENDER_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </td>
            <td className="px-3 py-3">
                <div className="flex items-center gap-1">
                    <Input
                        className="h-9 w-14"
                        type="number"
                        min={0}
                        value={draft.min_days}
                        onChange={(e) => set("min_days", e.target.value)}
                        placeholder="min"
                        data-testid={`leave-type-min-${t.code}`}
                    />
                    <span className="asl-text-3 text-xs">/</span>
                    <Input
                        className="h-9 w-14"
                        type="number"
                        min={0}
                        value={draft.max_days}
                        onChange={(e) => set("max_days", e.target.value)}
                        placeholder="max"
                        data-testid={`leave-type-max-${t.code}`}
                    />
                </div>
            </td>
            <td className="px-3 py-3">
                <Input
                    className="h-9"
                    value={draft.evidence_required}
                    onChange={(e) => set("evidence_required", e.target.value)}
                    placeholder="e.g. Medical certificate"
                    data-testid={`leave-type-evidence-${t.code}`}
                />
            </td>
            <td className="px-3 py-3">
                <div className="flex items-center gap-1.5">
                    <Checkbox
                        checked={!!draft.is_hourly}
                        onCheckedChange={(v) => set("is_hourly", !!v)}
                        data-testid={`leave-type-hourly-${t.code}`}
                    />
                    {draft.is_hourly && (
                        <Input
                            className="h-9 w-14"
                            type="number"
                            step="0.5"
                            value={draft.hours_per_day}
                            onChange={(e) => set("hours_per_day", e.target.value)}
                            placeholder="h/day"
                            data-testid={`leave-type-hours-${t.code}`}
                        />
                    )}
                </div>
            </td>
            <td className="px-3 py-3">
                <div
                    className="inline-flex items-center gap-1 text-[11px] asl-text-2"
                    title="Article references are locked — the law text does not change."
                >
                    <ScrollText className="h-3 w-3" />
                    <span className="mono">{t.article_reference || "—"}</span>
                </div>
            </td>
            <td className="px-3 py-3 text-center">
                <Checkbox
                    checked={!!draft.active}
                    onCheckedChange={(v) => set("active", !!v)}
                    data-testid={`leave-type-active-${t.code}`}
                />
            </td>
            <td className="px-3 py-3 text-right">
                <Button
                    size="sm"
                    variant={dirty ? "default" : "outline"}
                    disabled={!dirty || saving}
                    onClick={submit}
                    className={dirty ? "asl-btn-primary" : ""}
                    data-testid={`leave-type-save-${t.code}`}
                >
                    <Save className="h-3 w-3 mr-1" />
                    {saving ? "…" : "Save"}
                </Button>
            </td>
        </tr>
    );
}


function normaliseDraft(t) {
    return {
        paid: !!(t.paid ?? t.is_paid),
        entitled_days_per_year: toStrNum(t.entitled_days_per_year),
        accrual_days_per_month: toStrNum(t.accrual_days_per_month),
        tenure_gate_months: toStrNum(t.tenure_gate_months ?? 0),
        gender_restriction: t.gender_restriction || "__any__",
        min_days: toStrNum(t.min_days),
        max_days: toStrNum(t.max_days),
        evidence_required: t.evidence_required || "",
        is_hourly: !!t.is_hourly,
        hours_per_day: toStrNum(t.hours_per_day),
        duration_days: toStrNum(t.duration_days),
        active: !!(t.active ?? t.is_active ?? true),
    };
}


function CreateTypeDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({
        code: "", name: "", description: "",
        paid: true, entitled_days_per_year: "", accrual_days_per_month: "",
        tenure_gate_months: "0", gender_restriction: "__any__",
        min_days: "1", max_days: "", evidence_required: "",
        is_hourly: false, hours_per_day: "", duration_days: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                code: "", name: "", description: "",
                paid: true, entitled_days_per_year: "", accrual_days_per_month: "",
                tenure_gate_months: "0", gender_restriction: "__any__",
                min_days: "1", max_days: "", evidence_required: "",
                is_hourly: false, hours_per_day: "", duration_days: "",
            });
        }
    }, [open]);

    const submit = async () => {
        const code = form.code.trim().toLowerCase();
        if (!code || !/^[a-z][a-z0-9_]*$/.test(code)) {
            toast.error("Code must be lowercase, start with a letter (letters/numbers/underscore only).");
            return;
        }
        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/leave-types", {
                code,
                name: form.name.trim(),
                description: form.description.trim() || "",
                paid: !!form.paid,
                is_paid: !!form.paid,
                entitled_days_per_year: fromStrNum(form.entitled_days_per_year),
                accrual_days_per_month: fromStrNum(form.accrual_days_per_month),
                tenure_gate_months: fromStrNum(form.tenure_gate_months) || 0,
                gender_restriction: form.gender_restriction === "__any__" ? null : form.gender_restriction,
                min_days: fromStrNum(form.min_days),
                max_days: fromStrNum(form.max_days),
                evidence_required: form.evidence_required.trim() || null,
                is_hourly: !!form.is_hourly,
                hours_per_day: fromStrNum(form.hours_per_day),
                duration_days: fromStrNum(form.duration_days),
                active: true,
            });
            toast.success("Leave type created.");
            await onCreated();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid="leave-types-create-dialog"
            >
                <DialogHeader>
                    <DialogTitle>New Leave Type</DialogTitle>
                    <DialogDescription>
                        Custom leave type. Only Admin can create; HR + Admin can edit config on the table above.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                        <Label className="text-xs uppercase">Code</Label>
                        <Input
                            value={form.code}
                            onChange={(e) => set("code", e.target.value)}
                            placeholder="e.g. religious"
                            data-testid="leave-types-create-code"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            placeholder="Display name"
                            data-testid="leave-types-create-name"
                        />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs uppercase">Description</Label>
                        <Input
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            placeholder="Short description"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={!!form.paid}
                            onCheckedChange={(v) => set("paid", !!v)}
                            data-testid="leave-types-create-paid"
                        />
                        <Label>Paid</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={!!form.is_hourly}
                            onCheckedChange={(v) => set("is_hourly", !!v)}
                            data-testid="leave-types-create-hourly"
                        />
                        <Label>Hourly type</Label>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Entitled / year</Label>
                        <Input
                            type="number"
                            value={form.entitled_days_per_year}
                            onChange={(e) => set("entitled_days_per_year", e.target.value)}
                            placeholder="—"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Accrual / month</Label>
                        <Input
                            type="number" step="0.25"
                            value={form.accrual_days_per_month}
                            onChange={(e) => set("accrual_days_per_month", e.target.value)}
                            placeholder="—"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Tenure gate (months)</Label>
                        <Input
                            type="number" min={0}
                            value={form.tenure_gate_months}
                            onChange={(e) => set("tenure_gate_months", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Gender</Label>
                        <Select value={form.gender_restriction} onValueChange={(v) => set("gender_restriction", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {GENDER_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Min days</Label>
                        <Input
                            type="number" min={0}
                            value={form.min_days}
                            onChange={(e) => set("min_days", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Max days</Label>
                        <Input
                            type="number" min={0}
                            value={form.max_days}
                            onChange={(e) => set("max_days", e.target.value)}
                        />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs uppercase">Evidence required (label shown in request dialog)</Label>
                        <Input
                            value={form.evidence_required}
                            onChange={(e) => set("evidence_required", e.target.value)}
                            placeholder="e.g. Doctor's note (leave empty to skip)"
                        />
                    </div>
                    {form.is_hourly && (
                        <>
                            <div>
                                <Label className="text-xs uppercase">Hours / day</Label>
                                <Input
                                    type="number" step="0.5"
                                    value={form.hours_per_day}
                                    onChange={(e) => set("hours_per_day", e.target.value)}
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase">Duration (days)</Label>
                                <Input
                                    type="number" min={0}
                                    value={form.duration_days}
                                    onChange={(e) => set("duration_days", e.target.value)}
                                />
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="asl-btn-primary"
                        onClick={submit}
                        disabled={saving}
                        data-testid="leave-types-create-submit"
                    >
                        {saving ? "Creating…" : "Create leave type"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
